/** @jsxImportSource @emotion/react */
import { presentUserOrgRole } from '@kaiizen/kai-presenters';
import { ApiApiKeyRoles } from '@kaiizen/kai-pro-api';
import type {
  ApiApiKey,
  ApiApiKeyRole,
  ApiApiKeyStatus,
  ApiUserOrgRole,
} from '@kaiizen/kai-pro-api';
import { useApiKeys } from '@kaiizen/kai-pro-api-react-state';
import { CommonField } from '@kaiizen/kai-shiki/react';
import { useState } from 'react';

import { useConfirmationModal, useToast } from '@/Client/State';
import { LabeledSelect } from '@/Client/Widgets';
import { DEFAULT_API_KEYS_TAB_CONTENT_STYLES } from './apiKeysTabContentStyles';
import type { ApiKeysTabContentStyles } from './apiKeysTabContentStyles';
import { CreateApiKeyModal } from './CreateApiKeyModal';
import {
  ORG_SETTINGS_BODY_TEXT_STYLE,
  ORG_SETTINGS_CAPTION_STYLE,
  ORG_SETTINGS_PANEL_HEADER_STYLE,
  ORG_SETTINGS_PANEL_STYLE,
  ORG_SETTINGS_PRIMARY_ACTION_BUTTON_STYLE,
  ORG_SETTINGS_SUBHEADING_STYLE,
} from './OrgSettingsStyleLib';

/** Formats API timestamp values, which arrive in browser responses as ISO strings. */
const formatApiKeyDate = (date?: Date | string) => {
  if (!date) return 'Never';
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? 'Unavailable'
    : new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(parsedDate);
};

const ROLE_OPTIONS = ApiApiKeyRoles.map(role => ({ value: role, label: presentUserOrgRole(role) }));

const isApiKeyRole = (role: ApiUserOrgRole): role is ApiApiKeyRole => ApiApiKeyRoles.some(apiKeyRole => apiKeyRole === role);

const getStatusStyle = (status: ApiApiKeyStatus, styles: ApiKeysTabContentStyles) => [
  styles.status,
  status === 'active' && styles.activeStatus,
  status === 'revoked' && styles.revokedStatus,
];

const ApiKeyMetadata = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div css={ORG_SETTINGS_CAPTION_STYLE}>{label}</div>
    <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>{value}</div>
  </div>
);

const MachineUserCard = ({
  apiKeys,
  onCreateReplacement,
  styles,
}: {
  apiKeys: ApiApiKey[];
  onCreateReplacement: (machineUserId: string) => void;
  styles: ApiKeysTabContentStyles;
}) => {
  const apiKey = apiKeys[0]!;
  const { apiKeyMutators } = useApiKeys();
  const { tryWithErrorToast } = useToast();
  const confirm = useConfirmationModal();
  const [name, setName] = useState(apiKey.name);
  const [role, setRole] = useState(apiKey.role);
  const [isSaving, setIsSaving] = useState(false);
  const activeKeyCount = apiKeys.filter(key => key.status === 'active').length;
  const hasActiveKey = activeKeyCount > 0;
  const hasChanges = name.trim() !== apiKey.name || role !== apiKey.role;

  const saveChanges = async () => {
    if (!name.trim() || !hasChanges || isSaving) return;
    setIsSaving(true);
    try {
      await tryWithErrorToast('save the API key changes', () =>
        apiKeyMutators.update(apiKey.id, {
          ...(name.trim() !== apiKey.name ? { name: name.trim() } : {}),
          ...(role !== apiKey.role && isApiKeyRole(role) ? { role } : {}),
        }));
    } finally {
      setIsSaving(false);
    }
  };

  const revokeApiKey = async (key: ApiApiKey) =>
    confirm({
      title: `Revoke ${key.name}?`,
      message: 'This immediately blocks future requests. If this is the machine user’s last active key, its running jobs stop on their next API call.',
      confirmLabel: 'Revoke key',
      onConfirm: () => tryWithErrorToast('revoke the API key', () => apiKeyMutators.revoke(key.id)),
    });

  return (
    <article css={styles.machineUserCard}>
      <div css={styles.cardHeader}>
        <div>
          <div css={ORG_SETTINGS_CAPTION_STYLE}>Machine identity</div>
          <div css={ORG_SETTINGS_SUBHEADING_STYLE}>{apiKey.name}</div>
        </div>
        <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>{`${activeKeyCount} active ${activeKeyCount === 1 ? 'key' : 'keys'}`}</div>
      </div>
      <div css={styles.editForm}>
        <CommonField label='Name' inputProps={{ value: name, disabled: !hasActiveKey, onChange: event => setName(event.target.value) }} />
        <LabeledSelect
          label='Access role'
          value={role}
          items={ROLE_OPTIONS}
          disabled={!hasActiveKey}
          onUpdate={value => setRole(value as ApiApiKeyRole)}
        />
      </div>
      <div css={styles.actions}>
        <button
          css={styles.secondaryAction}
          disabled={!hasActiveKey || !name.trim() || !hasChanges || isSaving}
          onClick={saveChanges}
          type='button'
        >
          {isSaving ? 'Saving…' : 'Save changes'}
        </button>
        {activeKeyCount === 1 && (
          <button css={ORG_SETTINGS_PRIMARY_ACTION_BUTTON_STYLE} onClick={() => onCreateReplacement(apiKey.userId)} type='button'>
            Create replacement key
          </button>
        )}
      </div>
      <div css={styles.credentialList}>
        {apiKeys.map(key => (
          <section css={styles.credential} key={key.id}>
            <div css={styles.credentialHeader}>
              <div css={ORG_SETTINGS_SUBHEADING_STYLE}>API key</div>
              <div css={getStatusStyle(key.status, styles)}>{key.status}</div>
            </div>
            <div css={styles.metadata}>
              <ApiKeyMetadata label='Expires' value={formatApiKeyDate(key.expiresAt)} />
              <ApiKeyMetadata label='Last used' value={formatApiKeyDate(key.lastUsedAt)} />
              <ApiKeyMetadata label='Key status' value={key.status} />
            </div>
            {key.status === 'active' && (
              <div css={styles.actions}>
                <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Revoking blocks this key immediately.</div>
                <button css={styles.dangerAction} onClick={() => revokeApiKey(key)} type='button'>Revoke</button>
              </div>
            )}
          </section>
        ))}
      </div>
    </article>
  );
};

/** Allows organization administrators to create and manage their API-key machine identities. */
export const ApiKeysTabContent = ({
  styles = DEFAULT_API_KEYS_TAB_CONTENT_STYLES,
}: {
  styles?: ApiKeysTabContentStyles;
}) => {
  const { apiKeys, isLoading, error } = useApiKeys();
  const [apiKeyCreation, setApiKeyCreation] = useState<{ machineUserId?: string }>();
  const apiKeysByMachineUser = new Map<string, ApiApiKey[]>();
  for (const apiKey of apiKeys) {
    const keys = apiKeysByMachineUser.get(apiKey.userId) ?? [];
    keys.push(apiKey);
    apiKeysByMachineUser.set(apiKey.userId, keys);
  }
  const machineUserApiKeys = [...apiKeysByMachineUser.values()];

  return (
    <section css={[ORG_SETTINGS_PANEL_STYLE, styles.panel]}>
      <div css={ORG_SETTINGS_PANEL_HEADER_STYLE}>API Keys</div>
      <div css={styles.content}>
        <div css={styles.introduction}>
          <div>
            <div css={ORG_SETTINGS_SUBHEADING_STYLE}>Machine identities for integrations</div>
            <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Create one key per integration and give it only the access it needs.</div>
          </div>
          <button css={ORG_SETTINGS_PRIMARY_ACTION_BUTTON_STYLE} onClick={() => setApiKeyCreation({})} type='button'>Create API key</button>
        </div>
        <div css={styles.list}>
          <div css={styles.listHeader}>
            <div css={ORG_SETTINGS_SUBHEADING_STYLE}>Managed keys</div>
            {!isLoading && !error && <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>{`${apiKeys.length} total`}</div>}
          </div>
          {isLoading && <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Loading API keys…</div>}
          {error && <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Could not load API keys.</div>}
          {!isLoading && !error && apiKeys.length === 0 && (
            <div css={styles.emptyState}>
              <div css={ORG_SETTINGS_SUBHEADING_STYLE}>No API keys yet</div>
              <div css={ORG_SETTINGS_BODY_TEXT_STYLE}>Create a key when you are ready to connect an integration.</div>
            </div>
          )}
          {machineUserApiKeys.map(machineUserKeys => (
            <MachineUserCard
              apiKeys={machineUserKeys}
              key={machineUserKeys[0]!.userId}
              onCreateReplacement={machineUserId => setApiKeyCreation({ machineUserId })}
              styles={styles}
            />
          ))}
        </div>
      </div>
      {apiKeyCreation && (
        <CreateApiKeyModal
          machineUserId={apiKeyCreation.machineUserId}
          onClose={() => setApiKeyCreation(undefined)}
        />
      )}
    </section>
  );
};
